import katex from 'katex';
import rules from './rules.json';
export const normalizeQuestionType = type => rules.aliases[String(type).trim().toUpperCase()] || String(type).trim().toUpperCase();
export const questionTypeRule = type => rules.types[normalizeQuestionType(type)];
export function isFiniteNumericAnswer(value) {
  return (typeof value === 'string' || typeof value === 'number') &&
    new RegExp(rules.numberPattern).test(String(value).trim()) && Number.isFinite(Number(value));
}
export function validateQuestion(question, { approval = false, math = true } = {}) {
  const issues = [];
  const text = question.questionText ?? question.text ?? question.question ?? '';
  const type = normalizeQuestionType(question.type || question.questionType || 'MCQ_SINGLE_CORRECT');
  const rule = questionTypeRule(type);
  if (!String(text).trim() && !question.questionImageUrl) issues.push('Question text or image is required.');
  if (!rule) return [...issues, `Unsupported question type: ${type}.`];
  const options = (question.options || []).map(option => typeof option === 'string' ? { text: option } : (option || {}));
  if (rule.kind === 'options') {
    if (options.length < rule.minOptions || (rule.maxOptions && options.length > rule.maxOptions)) issues.push('Invalid number of answer options.');
    if (options.some(option => !option.text?.trim() && !option.imageUrl)) issues.push('Each option needs text or an image.');
    const flagged = options.flatMap((option, index) => option.isCorrect || option.correct ? [index + 1] : []);
    const rawKey = question.correctAnswer ?? (flagged.length ? flagged.join(',') : '');
    const keys = String(rawKey).split(',').map(value => Number(value.trim()));
    if (!String(rawKey).trim() || keys.some(key => !Number.isInteger(key) || key < 1 || key > options.length) || new Set(keys).size !== keys.length) issues.push('Answer key must reference distinct existing options.');
    else {
      if ((rule.correctCount && keys.length !== rule.correctCount) || (!rule.correctCount && !keys.length)) issues.push('The answer key has the wrong number of correct options.');
      if (options.some(option => Object.hasOwn(option, 'isCorrect') || Object.hasOwn(option, 'correct')) && (keys.length !== flagged.length || flagged.some(key => !keys.includes(key)))) issues.push('Answer key and marked correct options disagree.');
    }
  }
  if (rule.kind === 'numeric') {
    const answer = question.numericalAnswer ?? question.correctAnswer;
    if (!isFiniteNumericAnswer(answer)) issues.push('A finite numerical answer is required.');
  }
  if (rule.kind === 'essay' && (!question.essayPrompt?.trim() || !Number.isInteger(Number(question.essayMaxWords)) || Number(question.essayMaxWords) < 1)) issues.push('Essay prompt and a positive word limit are required.');
  if (rule.kind === 'matching' && ((question.matchingPairs || []).length < 2 || question.matchingPairs.some(pair => !pair.left?.trim() || !pair.right?.trim()))) issues.push('At least two complete matching pairs are required.');
  if (approval && !rule.studentSupported) issues.push(`${type} cannot be approved for student delivery until its answer controls are supported.`);
  if (type === 'READING_COMPREHENSION' && !question.passage?.trim() && !question.passageId?.trim()) issues.push('Reading comprehension requires a passage or passage ID.');
  if (math) {
    const fields = [['Question', text], ['Passage', question.passage], ['Explanation', question.explanation], ...options.map((option, index) => [`Option ${index + 1}`, option.text])];
    for (const [label, value] of fields) {
      const content = String(value || '');
      const expression = /(?<!\\)(\$\$|\$)([\s\S]*?)\1/g;
      let match;
      let remainder = content;
      while ((match = expression.exec(content))) {
        remainder = remainder.replace(match[0], '');
        try { katex.renderToString(match[2], { throwOnError: true, displayMode: match[1] === '$$', trust: false }); }
        catch { issues.push(`${label} contains invalid LaTeX.`); }
      }
      if (/(?<!\\)\$/.test(remainder)) issues.push(`${label} has unmatched math delimiters.`);
    }
  }
  return [...new Set(issues)];
}
export const questionImagePaths = question => [...new Set([
  question.questionImageUrl, question.explanationImageUrl,
  ...(question.options || []).map(option => typeof option === 'object' ? option.imageUrl : null)
].filter(Boolean))];
export async function validateQuestionImages(question, resolve, probe) {
  const results = await Promise.all(questionImagePaths(question).map(async path => {
    try { const url = await resolve(path); return url && await probe(url) ? null : `Image is unavailable: ${path}`; }
    catch { return `Image is unavailable: ${path}`; }
  }));
  return results.filter(Boolean);
}
export const probeImage = url => new Promise(resolve => {
  const img = new Image();
  const finish = result => { clearTimeout(timer); img.onload = null; img.onerror = null; resolve(result); };
  const timer = setTimeout(() => finish(false), 8000);
  img.onload = () => finish(img.naturalWidth > 0); img.onerror = () => finish(false); img.src = url;
});
